import { ICharacter, useUserSettings } from "@next-platform/client-common-core";
import { Button, ButtonSize, ButtonVariant, ListEmpty } from "@next-platform/client-common-spicy-ui";
import React, { useRef } from "react";
import { useHits, useInstantSearch } from "react-instantsearch";

import { Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import useAuth from "@/services/useAuth";
import { filterOutNsfwCharacters } from "@/utils/getCharacterHits";

import { CharacterItemAction, DataSourceType, DisplayContext } from "../../types";
import { HorizontalSelectableCard } from "../CharacterCards";
import { useMyChatbotsSearch } from "../contexts/MyChatbotsSearchContext";
import { useCharacterData } from "../hooks";

interface ISelectableHitsProps {
  onItemAction?: (action: CharacterItemAction, characterItem: ICharacter | undefined) => void;
  selectedItems: string[];
  maxItemsToSelect?: number;
  onLimitReached?: (currentCount: number, maxCount: number) => void;
  isPartner: boolean;
  dataSourceType?: DataSourceType;
  emptyStateDescription?: string | null;
  charHits?: ICharacter[] | null;
  isDataLoading?: boolean;
  shouldFilterNsfwCharacters: boolean;
}

const SelectableHitsList = ({
  charHits,
  isDataLoading,
  onItemAction,
  selectedItems,
  maxItemsToSelect,
  onLimitReached,
  isPartner,
  dataSourceType,
  emptyStateDescription,
}: ISelectableHitsProps) => {
  const refCharacterCardContainer = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useAuth();
  const { likedBots, blurNSFW, chatLanguage } = useUserSettings();

  const memoizedData = useCharacterData({
    charHits: charHits || [],
    likedBots,
    chatLanguage,
    dislikedRecommendedCharacterIds: [],
    displayContext: DisplayContext.MEMBER_SELECT,
    isPartner,
    blurNSFW,
    isAuthenticated,
  });

  const emptyState = {
    title: "No Results Found",
    description: emptyStateDescription || "Try clearing your filters or use another keyword.",
  };

  const hasNoResult = !isDataLoading && charHits?.length === 0;
  const shouldShowLoading = isDataLoading || hasNoResult;

  return (
    <div ref={refCharacterCardContainer}>
      {shouldShowLoading ? (
        <div className="grow flex justify-center">
          <ListEmpty isLoading={!!isDataLoading} title={emptyState.title} description={emptyState.description} />
        </div>
      ) : (
        <div className="flex flex-wrap gap-[18px] items-start justify-start overflow-clip relative shrink-0 w-full">
          {memoizedData.map((dataItem) => {
            const isSelected = selectedItems.includes(dataItem.characterId);
            const isLimitReached = maxItemsToSelect !== undefined && selectedItems.length >= maxItemsToSelect;

            return (
              <HorizontalSelectableCard
                key={dataItem.characterId}
                characterData={dataItem}
                showVisibilityBadge={dataSourceType === DataSourceType.MY_CHATBOTS}
                isSelected={isSelected}
                isDisabled={isLimitReached}
                onToggle={(action) => {
                  if (action === CharacterItemAction.ADD && isLimitReached && !isSelected) {
                    onLimitReached?.(selectedItems.length, maxItemsToSelect!);
                    return;
                  }
                  const charData = charHits?.find((char) => (char?.character_id || char?.id) === dataItem.characterId);
                  onItemAction?.(action, charData);
                }}
                onLimitReached={() => onLimitReached?.(selectedItems.length, maxItemsToSelect!)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const MyChatbotsSelectableHits = (props: ISelectableHitsProps) => {
  const { filteredAndSortedChatbots, isLoading, loadMoreChatbots, shouldShowLoadMoreButton, isLoadingMoreGroupchats } = useMyChatbotsSearch();
  const isDataLoading = isLoading || !filteredAndSortedChatbots;
  return (
    <>
      <SelectableHitsList {...props} charHits={filteredAndSortedChatbots} isDataLoading={isDataLoading} />
      {Boolean(shouldShowLoadMoreButton) && (
        <Row justify="center" className="my-lg">
          <Button
            loading={isLoadingMoreGroupchats}
            onClick={() => void loadMoreChatbots()}
            size={ButtonSize.LARGE}
            variant={ButtonVariant.OUTLINED}
            data-testid="SelectableHits-LoadMoreButton"
          >
            <Trans i18nKey="common:loadMore" />
          </Button>
        </Row>
      )}
    </>
  );
};

const SharedSelectableHits = (props: ISelectableHitsProps) => {
  const { items: hits } = useHits();
  const searchStatus = useInstantSearch({ catchError: true });

  const isHitsLoading = searchStatus?.status === "loading" || searchStatus?.status === "stalled";
  const rawCharHits = hits as unknown as ICharacter[];
  const charHits = props.shouldFilterNsfwCharacters ? filterOutNsfwCharacters(rawCharHits) : rawCharHits;

  const isDataLoading = !charHits || (isHitsLoading && charHits?.length === 0);

  return <SelectableHitsList {...props} charHits={charHits} isDataLoading={isDataLoading} />;
};

const SelectableHits = (props: ISelectableHitsProps) => {
  if (props.dataSourceType === DataSourceType.MY_CHATBOTS) {
    return <MyChatbotsSelectableHits {...props} />;
  }

  return <SharedSelectableHits {...props} />;
};

export default SelectableHits;
